/**
 * RevenueCat In-App Purchases Integration
 * Handles iOS/Android subscriptions and one-time purchases via RevenueCat
 *
 * Uses dynamic imports so Vite dev (web) can run without native-only packages.
 * On native, modules are loaded at runtime when these functions run.
 */

import { Capacitor } from '@capacitor/core'
import { supabase } from '@/lib/supabase'
import type {
  PurchasesPackage,
  CustomerInfo,
  PurchasesOffering,
} from '@revenuecat/purchases-capacitor'

async function getPurchases() {
  const { Purchases } = await import('@revenuecat/purchases-capacitor')
  return Purchases
}

async function getRevenueCatUI() {
  const { RevenueCatUI } = await import('@revenuecat/purchases-capacitor-ui')
  return RevenueCatUI
}

// ─── Configuration ───────────────────────────────────────────────────────────

// RevenueCat entitlement identifier (must match what you created in RC dashboard)
export const ENTITLEMENT_ID = 'Miac Pro'

// RevenueCat API keys from env
const REVENUECAT_IOS_KEY = import.meta.env.VITE_REVENUECAT_IOS_KEY || ''
const REVENUECAT_ANDROID_KEY = import.meta.env.VITE_REVENUECAT_ANDROID_KEY || ''

let isInitialized = false

// ─── Initialization ──────────────────────────────────────────────────────────

/**
 * Initialize RevenueCat SDK.
 * Call this once during native feature initialization.
 */
export async function initPurchases(): Promise<boolean> {
  if (isInitialized) {
    console.log('[Purchases] Already initialized')
    return true
  }

  if (!Capacitor.isNativePlatform()) {
    console.log('[Purchases] Not on native platform, skipping')
    return false
  }

  const platform = Capacitor.getPlatform()
  const apiKey = platform === 'ios' ? REVENUECAT_IOS_KEY : REVENUECAT_ANDROID_KEY
  console.log('[Purchases] init: platform:', platform, 'hasKey:', !!apiKey, 'keyPrefix:', apiKey.substring(0, 5))

  if (!apiKey) {
    console.warn('[Purchases] No API key configured for platform:', platform)
    return false
  }

  try {
    console.log('[Purchases] init: getting user...')
    const { data: { user } } = await supabase.auth.getUser()
    console.log('[Purchases] init: user:', user?.id ?? 'anonymous')

    console.log('[Purchases] init: calling Purchases.configure()...')
    const Purchases = await getPurchases()
    await Purchases.configure({ apiKey, appUserID: user?.id || undefined })

    isInitialized = true
    console.log('[Purchases] init: DONE, initialized for user:', user?.id)
    return true
  } catch (error) {
    console.error('[Purchases] init FAILED:', error)
    return false
  }
}

// ─── User identity ───────────────────────────────────────────────────────────

/**
 * Log in a user to RevenueCat (call after Supabase sign-in)
 */
export async function loginPurchases(userId: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  try {
    const Purchases = await getPurchases()
    await Purchases.logIn({ appUserID: userId })
    console.log('[Purchases] Logged in user:', userId)
  } catch (error) {
    console.error('[Purchases] Failed to log in:', error)
  }
}

/**
 * Log out from RevenueCat (call on Supabase sign-out)
 */
export async function logoutPurchases(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  try {
    const Purchases = await getPurchases()
    await Purchases.logOut()
    console.log('[Purchases] Logged out')
  } catch (error) {
    console.error('[Purchases] Failed to log out:', error)
  }
}

// ─── Offerings & purchasing ──────────────────────────────────────────────────

/**
 * Get available offerings (subscription plans + one-time packs)
 */
export async function getOfferings(): Promise<PurchasesOffering | null> {
  if (!Capacitor.isNativePlatform()) return null

  try {
    const Purchases = await getPurchases()
    const offerings = await Purchases.getOfferings()
    return offerings.current as PurchasesOffering | null
  } catch (error) {
    console.error('[Purchases] Failed to get offerings:', error)
    return null
  }
}

/**
 * Purchase a subscription or one-time product
 */
export async function purchasePackage(pkg: PurchasesPackage): Promise<{
  success: boolean
  customerInfo?: CustomerInfo
  error?: string
}> {
  if (!Capacitor.isNativePlatform()) return { success: false, error: 'Purchases not available' }

  try {
    const Purchases = await getPurchases()
    const result = await Purchases.purchasePackage({ aPackage: pkg })
    console.log('[Purchases] Purchase successful:', result)
    return {
      success: true,
      customerInfo: result.customerInfo as unknown as CustomerInfo,
    }
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string; userCancelled?: boolean }

    if (err.userCancelled || err.code === '1') {
      return { success: false, error: 'cancelled' }
    }

    console.error('[Purchases] Purchase failed:', error)
    return {
      success: false,
      error: err.message || 'Purchase failed. Please try again.',
    }
  }
}

/**
 * Restore previous purchases (required by Apple)
 */
export async function restorePurchases(): Promise<{
  success: boolean
  customerInfo?: CustomerInfo
  error?: string
}> {
  if (!Capacitor.isNativePlatform()) return { success: false, error: 'Purchases not available' }

  try {
    const Purchases = await getPurchases()
    const result = await Purchases.restorePurchases()
    console.log('[Purchases] Restore successful:', result)
    return {
      success: true,
      customerInfo: result.customerInfo as unknown as CustomerInfo,
    }
  } catch (error: unknown) {
    const err = error as { message?: string }
    console.error('[Purchases] Restore failed:', error)
    return {
      success: false,
      error: err.message || 'Failed to restore purchases.',
    }
  }
}

// ─── Customer info ───────────────────────────────────────────────────────────

/**
 * Get current customer info (subscription status from RevenueCat)
 */
export async function getCustomerInfo(): Promise<CustomerInfo | null> {
  if (!Capacitor.isNativePlatform()) return null

  try {
    const Purchases = await getPurchases()
    const result = await Purchases.getCustomerInfo()
    return result.customerInfo as unknown as CustomerInfo
  } catch (error) {
    console.error('[Purchases] Failed to get customer info:', error)
    return null
  }
}

/**
 * Check if user has the "Miac Pro" entitlement (local check via RevenueCat)
 */
export async function hasPremiumEntitlement(): Promise<boolean> {
  const customerInfo = await getCustomerInfo()
  if (!customerInfo) return false
  return !!customerInfo.entitlements.active[ENTITLEMENT_ID]?.isActive
}

// ─── RevenueCat UI: Native Paywall ──────────────────────────────────────────

/**
 * Present RevenueCat's native paywall.
 * This renders a paywall you designed in the RC dashboard.
 * Returns true if the user now has the entitlement, false if dismissed.
 */
export async function presentPaywall(): Promise<{
  presented: boolean
  purchased: boolean
}> {
  console.log('[Purchases] presentPaywall called, isInitialized:', isInitialized)

  if (!Capacitor.isNativePlatform()) {
    console.log('[Purchases] Not on native, skipping paywall')
    return { presented: false, purchased: false }
  }

  // Ensure SDK is initialized
  if (!isInitialized) {
    console.log('[Purchases] SDK not initialized, attempting init...')
    const initResult = await initPurchases()
    console.log('[Purchases] Init result:', initResult)
    if (!initResult) {
      console.error('[Purchases] SDK failed to initialize')
      return { presented: false, purchased: false }
    }
  }

  // Fetch offerings to verify we have products
  try {
    const Purchases = await getPurchases()
    const offerings = await Purchases.getOfferings()
    console.log('[Purchases] Current offering:', offerings.current?.identifier ?? 'NONE')
    console.log('[Purchases] Available packages:', offerings.current?.availablePackages?.length ?? 0)
    if (!offerings.current || !offerings.current.availablePackages?.length) {
      console.error('[Purchases] No offerings/packages available - check RevenueCat dashboard')
      return { presented: false, purchased: false }
    }
  } catch (offerErr) {
    console.error('[Purchases] Failed to fetch offerings:', offerErr)
    return { presented: false, purchased: false }
  }

  try {
    const RevenueCatUI = await getRevenueCatUI()
    console.log('[Purchases] Calling RevenueCatUI.presentPaywall()...')
    const result = await RevenueCatUI.presentPaywall()
    console.log('[Purchases] Paywall result:', JSON.stringify(result))
    return {
      presented: true,
      purchased: result.result === 'PURCHASED' || result.result === 'RESTORED',
    }
  } catch (error) {
    console.error('[Purchases] Failed to present paywall:', error)
    return { presented: false, purchased: false }
  }
}

/**
 * Present RevenueCat's native paywall only if the user
 * does NOT have the "Miac Pro" entitlement.
 * Returns true if the user now has the entitlement.
 */
export async function presentPaywallIfNeeded(): Promise<{
  presented: boolean
  purchased: boolean
}> {
  if (!Capacitor.isNativePlatform()) return { presented: false, purchased: false }

  try {
    const RevenueCatUI = await getRevenueCatUI()
    const result = await RevenueCatUI.presentPaywallIfNeeded({
      requiredEntitlementIdentifier: ENTITLEMENT_ID,
    })
    console.log('[Purchases] PaywallIfNeeded result:', result)
    return {
      presented: result.result !== 'NOT_PRESENTED',
      purchased: result.result === 'PURCHASED' || result.result === 'RESTORED',
    }
  } catch (error) {
    console.error('[Purchases] Failed to present paywall:', error)
    return { presented: false, purchased: false }
  }
}

// ─── RevenueCat UI: Customer Center ─────────────────────────────────────────

/**
 * Present RevenueCat's Customer Center.
 * Users can manage their subscription, request refunds, restore purchases, etc.
 * Available on Pro/Enterprise RC plans.
 */
export async function presentCustomerCenter(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) {
    console.warn('[Purchases] Customer Center not available (not native)')
    return false
  }

  try {
    const RevenueCatUI = await getRevenueCatUI()
    await RevenueCatUI.presentCustomerCenter()
    console.log('[Purchases] Customer Center presented')
    return true
  } catch (error) {
    console.error('[Purchases] Failed to present Customer Center:', error)
    return false
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Check if RevenueCat is available on this platform
 */
export function isPurchasesAvailable(): boolean {
  return Capacitor.isNativePlatform()
}
